import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MANIFESTO_STATEMENTS } from '../../data/ecoContent';

gsap.registerPlugin(ScrollTrigger);

export const EcologicalManifesto: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textElementsRef = useRef<(HTMLParagraphElement | null)[]>([]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const lines = textElementsRef.current.filter(Boolean) as HTMLParagraphElement[];
    if (lines.length === 0) return;

    const ctx = gsap.context(() => {
      lines.forEach((line) => {
        gsap.fromTo(
          line,
          {
            color: 'rgba(10, 28, 20, 0.20)',
            y: 10,
          },
          {
            color: '#0A1C14',
            y: 0,
            ease: 'none',
            scrollTrigger: {
              trigger: line,
              start: 'top 82%',
              end: 'top 48%',
              scrub: 0.6,
            },
          }
        );
      });
    }, container);

    return () => ctx.revert();
  }, []);

  const romanNumerals = ['§ I', '§ II', '§ III', '§ IV', '§ V', '§ VI', '§ VII', '§ VIII', '§ IX'];

  return (
    <section
      id="manifesto"
      ref={containerRef}
      className="relative py-28 sm:py-36 px-4 sm:px-6 z-10 border-t border-carbon-900/10 bg-alabaster-200/50"
    >
      <div className="max-w-5xl mx-auto space-y-12 sm:space-y-16">
        {/* Section Header */}
        <div className="flex items-center justify-between pb-4 border-b border-carbon-900/10 font-mono text-xs">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-chlorophyll inline-block" />
            <span className="font-bold text-carbon-900 tracking-widest uppercase">
              02 // THE ARCHIVAL MANIFESTO
            </span>
          </div>
          <span className="text-carbon-900/50">THERMODYNAMICS OF SCALE</span>
        </div>

        {/* Scrubbing Text Track */}
        <div className="space-y-8 sm:space-y-12 font-serif text-2xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.18]">
          {MANIFESTO_STATEMENTS.map((statement, idx) => {
            const isHighlight =
              idx === MANIFESTO_STATEMENTS.length - 2 || idx === MANIFESTO_STATEMENTS.length - 1;
            return (
              <div key={idx} className="flex items-baseline gap-4 sm:gap-8">
                <span className="font-mono text-xs sm:text-sm text-carbon-900/40 shrink-0 w-8 sm:w-12">
                  {romanNumerals[idx] || '§'}
                </span>
                <p
                  ref={(el) => (textElementsRef.current[idx] = el)}
                  className={`transition-colors duration-200 ${
                    isHighlight ? 'font-bold text-chlorophyll-deep italic' : ''
                  }`}
                >
                  {statement}
                </p>
              </div>
            );
          })}
        </div>

        {/* Archival Footnote */}
        <div className="pt-8 border-t border-carbon-900/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4 font-mono text-xs text-carbon-900/50">
          <div>PRINCIPLE: ENTROPY REVERSAL THROUGH SYSTEMIC REFORESTATION</div>
          <div>ESTABLISHED 2026 // GAIA RESEARCH CONSORTIUM</div>
        </div>
      </div>
    </section>
  );
};
